import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgentDashboard, MonthlyReport, SessionDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { dayLabel, euro, fmtClock, fmtLongDate, hm, localDay, monthLabel, thisMonth, todayKey } from '../format';
import { Avatar, Calendar, ClockCard, Empty, ErrorBanner, Panel, Pill, Row, Section, Seg, SessionsList, Stat, useNow, useSections } from '../ui';

type Filter = 'live' | 'month' | 'day';

/** Dashboard del commerciale: clienti seguiti, ore, provvigioni e calendario delle sessioni. */
export function AgentView({ ctx }: { ctx: Ctx }) {
  const { api, event, eventSeq } = ctx;
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState<AgentDashboard | null>(null);
  const [sessions, setSessions] = useState<SessionDto[]>([]);
  const [filter, setFilter] = useState<Filter>('live');
  const [day, setDay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sections = useSections({ clients: true, today: true });
  const now = useNow(1000);
  const today = todayKey();

  const load = useCallback(async () => {
    const d = await api.get<AgentDashboard>(`/api/agent/dashboard?month=${month}`);
    setData(d);
    const reports = await Promise.all(d.rows.map((r) => api.get<MonthlyReport>(`/api/reports/monthly?clientId=${r.clientId}&month=${month}`)));
    setSessions(reports.flatMap((r) => r.sessions).sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1)));
    setError(null);
  }, [api, month]);

  useEffect(() => {
    load().catch((e) => setError((e as Error).message));
  }, [load]);

  useEffect(() => {
    if (!event) return;
    if (event.type === 'session.started' || event.type === 'session.stopped') load().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSeq]);

  // Aggiornamento periodico di sicurezza (se il WebSocket dovesse cadere).
  useEffect(() => {
    const t = setInterval(() => load().catch(() => undefined), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const onlineRows = data?.rows.filter((r) => r.online) ?? [];
  useEffect(() => {
    const online = onlineRows.length > 0;
    window.tt.setTray(online ? 'running' : 'idle', online ? `In sessione: ${onlineRows.map((r) => r.clientName).join(', ')}` : 'CRM TecnoSide');
  }, [onlineRows.map((r) => r.clientId).join(',')]);

  const byDay = useMemo(() => {
    const m: Record<string, SessionDto[]> = {};
    for (const s of sessions) (m[localDay(s.startedAt)] ??= []).push(s);
    return m;
  }, [sessions]);
  const marks = useMemo(() => {
    const m: Record<string, true> = {};
    for (const k of Object.keys(byDay)) m[k] = true;
    if (onlineRows.length) m[today] = true;
    return m;
  }, [byDay, onlineRows.length, today]);

  const todaySessions = month === thisMonth() ? (byDay[today] ?? []) : [];
  const listed = filter === 'day' && day ? (byDay[day] ?? []) : sessions;
  const listedMinutes = listed.reduce((a, s) => a + s.totalMinutes, 0);
  const listedAmount = listed.reduce((a, s) => a + s.amountCents, 0);

  const goToday = () => {
    setMonth(thisMonth());
    setDay(today);
    setFilter('day');
  };
  const pickDay = (k: string) => {
    if (k.slice(0, 7) !== month) setMonth(k.slice(0, 7));
    setDay(k);
    setFilter('day');
  };

  const subtitle =
    filter === 'live'
      ? onlineRows.length
        ? `In sessione: ${onlineRows.map((r) => r.clientName).join(', ')}`
        : 'Nessuna sessione in corso'
      : filter === 'day' && day
        ? dayLabel(day)
        : monthLabel(month);

  return (
    <div className="panels">
      <Panel title="Ore dei tuoi clienti" subtitle="Quando il consulente lavora per i clienti che segui">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div>
          <Seg icon="inbox" label="In sessione ora" count={onlineRows.length} tone={onlineRows.length ? 'good' : undefined} active={filter === 'live'} onClick={() => setFilter('live')} />
          <Seg icon="checkCircle" label="Oggi" count={todaySessions.length} active={filter === 'day' && day === today} onClick={goToday} />
          <Seg icon="layers" label="Tutto il mese" count={sessions.length} active={filter === 'month'} onClick={() => setFilter('month')} />
        </div>

        <ClockCard
          now={now}
          on={onlineRows.length > 0}
          headline={fmtClock(now)}
          sub={
            onlineRows.length ? (
              <>
                <b>{onlineRows.map((r) => r.clientName).join(', ')}</b> · il consulente sta lavorando
              </>
            ) : (
              fmtLongDate(now)
            )
          }
        />

        <Calendar
          month={month}
          selected={filter === 'day' ? day : null}
          marks={marks}
          onSelect={pickDay}
          onMonth={(m) => {
            setMonth(m);
            setDay(null);
            setFilter('month');
          }}
        />
      </Panel>

      <Panel title="Attività" subtitle={subtitle}>
        {!data ? (
          <div className="muted small">Caricamento…</div>
        ) : filter === 'live' ? (
          <>
            <div className="stats">
              <Stat value={hm(data.totals.minutes)} label="ore lavorate nel mese" />
              <Stat value={euro(data.totals.amountCents)} label="fatturabile ai clienti" />
              <Stat value={euro(data.totals.commissionCents)} label="tue provvigioni" navy />
            </div>
            <Section title="I tuoi clienti" count={data.rows.length} open={sections.open.clients} onToggle={() => sections.toggle('clients')}>
              {data.rows.length === 0 ? (
                <Empty icon="building">Nessun cliente assegnato: te li collega il consulente.</Empty>
              ) : (
                data.rows.map((r) => (
                  <Row key={r.clientId}>
                    <Avatar name={r.clientName} on={r.online} />
                    <div className="grow">
                      <div className="title">{r.clientName}</div>
                      <div className="sub hstack" style={{ gap: 6 }}>
                        {r.isReferrer && <Pill tone="info" icon="star" title="Hai portato tu questo cliente">segnalazione</Pill>}
                        {r.isManager && <Pill tone="warn" icon="shield" title="Segui tu questo cliente">gestione</Pill>}
                        <span>
                          {hm(r.minutes)} · {euro(r.amountCents)}
                        </span>
                      </div>
                    </div>
                    {r.online && <Pill tone="good" icon="checkCircle">in sessione</Pill>}
                    <span className="pill" title="Le tue provvigioni di questo mese per questo cliente">{euro(r.referralCents + r.managementCents)}</span>
                    <button type="button" className="btn icon small ghost" title="Dettaglio delle ore del mese (serve per la fattura)" onClick={() => ctx.setPage('reports', { clientId: r.clientId })}>
                      <Icon name="list" size={16} />
                    </button>
                  </Row>
                ))
              )}
            </Section>
            <Section title="Sessioni di oggi" count={todaySessions.length} open={sections.open.today} onToggle={() => sections.toggle('today')}>
              <SessionsList api={api} sessions={todaySessions} emptyText="Nessuna sessione conclusa oggi." />
            </Section>
          </>
        ) : (
          <>
            <div className="stats">
              <Stat value={listed.length} label="sessioni" />
              <Stat value={hm(listedMinutes)} label="ore" />
              <Stat value={euro(listedAmount)} label="fatturabile" navy />
            </div>
            <SessionsList api={api} sessions={listed} />
          </>
        )}
      </Panel>
    </div>
  );
}
